// @ts-nocheck
import React from 'react';

// ErrorBoundary global — rede de segurança contra "tela branca".
//
// Sem isto, QUALQUER exceção de renderização (ou falha ao baixar um chunk
// lazy após um deploy) derruba toda a árvore React e o usuário vê uma tela
// totalmente branca, sem nem uma mensagem.
//
// Dois casos tratados:
// 1) ChunkLoadError (bundle antigo em cache tentando baixar um arquivo que
//    mudou de hash após deploy): recarrega a página UMA vez para buscar a
//    versão nova. Trava anti-loop via sessionStorage — se já recarregou há
//    pouco e ainda falhou, mostra a tela de erro com botão manual em vez de
//    ficar recarregando sem parar.
// 2) Qualquer outro erro de render: mostra uma tela amigável com botão
//    "Recarregar", em vez de tela branca.
//
// Em operação normal este componente é invisível e não altera nada.

const RELOAD_FLAG = 'agendali_chunk_reload_ts';
const RELOAD_WINDOW_MS = 15000; // não recarrega de novo se já recarregou nos últimos 15s

function isChunkLoadError(error: any): boolean {
  if (!error) return false;
  const name = String(error.name || '');
  const msg = String(error.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg)
  );
}

export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; isChunk: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, isChunk: false };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, isChunk: isChunkLoadError(error) };
  }

  componentDidCatch(error: any) {
    if (isChunkLoadError(error)) {
      let last = 0;
      try { last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0); } catch (_) {}
      const now = Date.now();
      // Só recarrega se não recarregou recentemente (evita loop infinito de reload).
      if (now - last > RELOAD_WINDOW_MS) {
        try { sessionStorage.setItem(RELOAD_FLAG, String(now)); } catch (_) {}
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch (_) {}
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Para ChunkLoadError recente, o reload já foi disparado; mostra um aviso
      // neutro de transição em vez de piscar a tela de erro.
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 24, textAlign: 'center', fontFamily: 'Inter, sans-serif', color: '#374151' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
            {this.state.isChunk ? 'Atualizando para a versão mais recente…' : 'Não foi possível carregar esta tela'}
          </div>
          <div style={{ fontSize: 13.5, color: '#6b7280', marginBottom: 20, maxWidth: 320, lineHeight: 1.5 }}>
            {this.state.isChunk
              ? 'Se esta mensagem não sumir em alguns segundos, toque no botão abaixo.'
              : 'Tente recarregar a página. Se o problema continuar, entre em contato com o suporte.'}
          </div>
          <button
            onClick={this.handleReload}
            style={{ background: '#2563EB', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
          >
            Recarregar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
